// Sections 6, 8, 16 and 23: the body-and-mind state of a person as modifiers. Strength drives
// output through f(S); need satisfaction drives strength change, health gain, illness risk and
// the happiness target.
import { defineContributor } from '../../core/registry';
import { mod } from '../../core/modifiers';
import { piecewise, powInt } from '../../core/math';
import { memoByState } from '../../core/memo';
import { needsOf, settlementTrust } from '../../core/queries';
import type { Modifier, NeedSatisfaction, ReadContext } from '../../core/types';

function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

function isCold(ctx: ReadContext, needs: NeedSatisfaction): boolean {
  return (
    ctx.cfg.needs.warmthPerBed[ctx.season.season] > 0 && needs.warmth < ctx.cfg.needs.shortThreshold
  );
}

function allMet(ctx: ReadContext, n: NeedSatisfaction): boolean {
  const t = ctx.cfg.needs.metThreshold;
  return n.food >= t && n.water >= t && n.shelter >= t && n.warmth >= t;
}

export const needsContributor = defineContributor({
  slot: 'needs',
  id: 'needs:core',
  targets: ['output', 'illnessRisk', 'strengthChange', 'healthGain', 'happinessTarget', 'demand'],
  modifiers(ctx, target) {
    if (!('personId' in target)) return [];
    const p = ctx.state.people[target.personId];
    if (p === undefined) return [];
    const cfg = ctx.cfg;
    const n = needsOf(ctx.state, p.id);
    const out: Modifier[] = [];
    switch (target.kind) {
      case 'output': {
        out.push(
          mod.mul(
            'body:strength',
            piecewise(cfg.work.strengthCurve, p.strength),
            `strength ${Math.round(p.strength)}`,
          ),
        );
        const h =
          cfg.work.happinessBase + (cfg.work.happinessSpan * p.happiness) / cfg.people.statMax;
        out.push(mod.mul('mind:happiness', h, `happiness ${Math.round(p.happiness)}`));
        if (
          memoByState(ctx.state, 'settlement-trust', () => settlementTrust(ctx.state)) >
          cfg.work.trustBonusAbove
        ) {
          out.push(mod.mul('society:trust', cfg.work.trustBonusOutput, 'high trust'));
        }
        return out;
      }
      case 'illnessRisk': {
        const short = cfg.needs.shortThreshold;
        if (n.food < short) out.push(mod.mul('need:food', cfg.illness.hungryMul, 'hungry'));
        if (n.water < short)
          out.push(mod.mul('need:water', cfg.illness.dehydratedMul, 'dehydrated'));
        if (isCold(ctx, n)) out.push(mod.mul('need:warmth', cfg.illness.coldMul, 'cold'));
        if (n.shelter < short)
          out.push(mod.mul('need:shelter', cfg.illness.noShelterMul, 'no shelter'));
        // Section 12 crowding: 1.3x per person over bed capacity in the household (capped, D-12).
        const extra = ctx.state.currentReport?.crowding[p.householdId] ?? 0;
        if (extra > 0) {
          const b = cfg.building;
          const factor = Math.min(b.crowdingIllnessCap, powInt(b.crowdingIllnessPerExtra, extra));
          out.push(mod.mul('need:shelter', factor, `${extra} over bed capacity`));
        }
        return out;
      }
      case 'strengthChange': {
        const b = cfg.bodies;
        out.push(
          mod.add(
            'need:food',
            b.strengthFoodGain * n.food - b.strengthFoodLoss * (1 - n.food),
            `fed ${pct(n.food)}`,
          ),
        );
        out.push(
          mod.add(
            'need:water',
            b.strengthWaterGain * n.water - b.strengthWaterLoss * (1 - n.water),
            `water ${pct(n.water)}`,
          ),
        );
        if (isCold(ctx, n)) out.push(mod.add('need:warmth', -b.strengthCold, 'cold'));
        return out;
      }
      case 'demand': {
        // Section 6 and 17: warmth is per household bed, not per person. A household squeezed
        // into fewer beds than members heats only the beds it has (rough sleepers need their own fire).
        if (target.resource !== 'warmth') return [];
        const household = ctx.state.households[p.householdId];
        const extra = ctx.state.currentReport?.crowding[p.householdId] ?? 0;
        if (household === undefined || household.buildingId === null || extra <= 0) return [];
        const size = household.memberIds.length;
        return [mod.mul('need:shelter', (size - extra) / size, 'sharing beds')];
      }
      case 'healthGain':
        return allMet(ctx, n)
          ? [mod.add('need:all', cfg.bodies.healthAllNeedsMet, 'all needs met')]
          : [];
      case 'happinessTarget': {
        const h = cfg.happiness;
        const m = (n.food + n.water + n.shelter + n.warmth) / 4;
        out.push(mod.add('need:material', h.needsSpan * (2 * m - 1), `needs ${pct(m)}`));
        out.push(
          mod.add('need:safety', h.safetySpan * (2 * n.safety - 1), `safety ${pct(n.safety)}`),
        );
        out.push(
          mod.add(
            'need:belonging',
            h.belongingSpan * (2 * n.belonging - 1),
            `belonging ${pct(n.belonging)}`,
          ),
        );
        return out;
      }
      default:
        return [];
    }
  },
});
